import '@angular/localize';

/**
 * Pure configuration object for the Dev Agent UI text and settings.
 * Uses Angular localize for i18n support.
 */
export const DEV_AGENT_CONFIG = {
  texts: {
    title: $localize`:@@devAgent.title:Rapider AI Dev Agent`,
    description: $localize`:@@devAgent.description:This AI agent will analyze your project data models and relationships, generate the necessary code for your project, build the server code, deploy it, and verify the deployment by running tests. This process may take a few minutes. You can still use the platform while the agent is working on generating and deploying your project code; however, the developer agent needs to generate your code and deploy your server before you can save any changes you make to the database or project models. You can still work with your data and models in read-only mode until the deployment is complete.`,
    successMessage: $localize`:@@devAgent.success:Your project code has been successfully generated and deployed. Your server is up and running. You can now start working with your data and models, and save your changes as needed!`,
    closeButton: $localize`:@@devAgent.closeButton:Close Rapider Dev Agent`,
    minimizedTag: $localize`:@@devAgent.minimizedTag:Rapider AI Dev Agent is working`,
    errorPrefix: $localize`:@@devAgent.errorPrefix:Process Failed:`,
    genericErrorMessage: $localize`:@@devAgent.genericErrorMessage:There was an issue with generating or deploying your code. Please try again. If the issue persists, please contact support.`,
    serverDetailsTitle: $localize`:@@devAgent.serverDetailsTitle:Server Details`,
    serverNameLabel: $localize`:@@devAgent.serverNameLabel:Server Name`,
    viewLogsButton: $localize`:@@devAgent.viewLogsButton:View Deployment Logs`,
    logsModalTitle: $localize`:@@devAgent.logsModalTitle:Deployment Logs`,
    noDeploymentIdError: $localize`:@@devAgent.noDeploymentIdError:No deployment ID found to fetch logs for.`,
    hideTimestamps: $localize`:@@devAgent.hideTimestamps:Hide timestamps`,
    showTimestamps: $localize`:@@devAgent.showTimestamps:Show timestamps`,

    // --- Drawer redesign texts ---
    subtitle: $localize`:@@devAgent.subtitle:Code generation & DevOps`,
    shortDescription: $localize`:@@devAgent.shortDescription:I read your data models and relationships, generate (or regenerate) your server and REST API code, migrate your database, then deploy and health-check your backend. It runs in the background so you can keep working.`,
    learnMore: $localize`:@@devAgent.learnMore:How does it work?`,
    capabilitiesTitle: $localize`:@@devAgent.capabilitiesTitle:What I can do`,
    selectActionTitle: $localize`:@@devAgent.selectActionTitle:Choose a job`,
    branchTitle: $localize`:@@devAgent.branchTitle:Git branch`,
    branchOptional: $localize`:@@devAgent.branchOptional:Optional`,
    branchPlaceholder: $localize`:@@devAgent.branchPlaceholder:Default is the active branch`,
    backendStatusTitle: $localize`:@@devAgent.backendStatusTitle:Backend status`,
    noContainers: $localize`:@@devAgent.noContainers:No active containers found.`,
    recentDeploymentsTitle: $localize`:@@devAgent.recentDeploymentsTitle:Recent deployments`,
    noDeployments: $localize`:@@devAgent.noDeployments:No recent deployments.`,
    latestVersion: $localize`:@@devAgent.latestVersion:Latest version`,
    systemUser: $localize`:@@devAgent.systemUser:System`,
    cancelButton: $localize`:@@devAgent.cancelButton:Cancel`,
    startButton: $localize`:@@devAgent.startButton:Start agent`,
    minimizeTooltip: $localize`:@@devAgent.minimizeTooltipText:Minimize to footer`,
    closeTooltip: $localize`:@@devAgent.closeTooltip:Close`,
    skipped: $localize`:@@devAgent.skipped:Skipped`,
    stateLabel: $localize`:@@devAgent.stateLabel:State`,
    statusLabel: $localize`:@@devAgent.statusLabel:Status`,
    statusAt: $localize`:@@devAgent.statusAt:at`,
    statusReady: $localize`:@@devAgent.statusReady:Ready`,
    statusRunning: $localize`:@@devAgent.statusRunning:Working`,
    statusDone: $localize`:@@devAgent.statusDone:Deployed`,
    statusFailed: $localize`:@@devAgent.statusFailed:Failed`,
    stepOf: $localize`:@@devAgent.stepOf:Step`,
    stepOfSeparator: $localize`:@@devAgent.stepOfSeparator:of`,
    backgroundHint: $localize`:@@devAgent.backgroundHint:Runs in the background. Minimize and keep working.`,
    pipelineTitle: $localize`:@@devAgent.pipelineTitle:Pipeline`,
    includesLabel: $localize`:@@devAgent.includesLabel:Includes`,
    closeWhileRunningTitle: $localize`:@@devAgent.closeWhileRunningTitle:Stop watching this run?`,
    closeWhileRunningContent: $localize`:@@devAgent.closeWhileRunningContent:The agent is still working. Closing the panel stops tracking progress here; jobs already started on the server may continue. Minimize instead to keep watching.`,
    closeWhileRunningOk: $localize`:@@devAgent.closeWhileRunningOk:Close anyway`,
    closeWhileRunningCancel: $localize`:@@devAgent.closeWhileRunningCancel:Keep open`,
  },
  storageKey: 'devAgentProcessStartTime',
  pollingIntervalMs: 10000,
  simulatedDelayMs: 2500,
  steps: [
    {
      id: 1,
      text: $localize`:@@devAgent.step1:Analyzing your project data models and relationships...`,
      phase: $localize`:@@devAgent.phase1:AI analysis`,
      icon: 'fa-solid fa-magnifying-glass-chart',
    },
    {
      id: 2,
      text: $localize`:@@devAgent.step2:Validating the version...`,
      phase: $localize`:@@devAgent.phase2:Versioning`,
      icon: 'fa-solid fa-code-compare',
    },
    {
      id: 3,
      text: $localize`:@@devAgent.step3:Migrating your database...`,
      phase: $localize`:@@devAgent.phase3:Database`,
      icon: 'fa-solid fa-database',
    },
    {
      id: 4,
      text: $localize`:@@devAgent.step4:Generating code for your project...`,
      phase: $localize`:@@devAgent.phase4:Server & API code`,
      icon: 'fa-solid fa-code',
    },
    {
      id: 5,
      text: $localize`:@@devAgent.step5:Deploying your server...`,
      phase: $localize`:@@devAgent.phase5:DevOps deploy`,
      icon: 'fa-solid fa-rocket',
    },
    {
      id: 6,
      text: $localize`:@@devAgent.step6:Starting the server...`,
      phase: $localize`:@@devAgent.phase6:Health check`,
      icon: 'fa-solid fa-heart-pulse',
    },
  ],
};

/** The three jobs the Dev Agent can run. */
export type DevAgentAction = 'generateAndDeploy' | 'generateOnly' | 'deployOnly';

/** Visual state of a single pipeline step. */
export type DevAgentStepState =
  | 'pending'
  | 'active'
  | 'done'
  | 'error'
  | 'skipped';

/** Overall run status shown in the drawer header. */
export type DevAgentRunStatus = 'ready' | 'running' | 'done' | 'failed';

/** A capability chip explaining what the agent can do. */
export interface DevAgentCapability {
  icon: string;
  title: string;
  description: string;
  tileClass: string;
}

/** Capabilities displayed on the configuration screen. */
export const DEV_AGENT_CAPABILITIES: readonly DevAgentCapability[] = [
  {
    icon: 'fa-solid fa-code',
    title: $localize`:@@devAgent.cap.code:Generate server code`,
    description: $localize`:@@devAgent.cap.codeDesc:Create or regenerate your backend from your models.`,
    tileClass: 'bg-blue-600',
  },
  {
    icon: 'fa-solid fa-plug',
    title: $localize`:@@devAgent.cap.api:Build REST APIs`,
    description: $localize`:@@devAgent.cap.apiDesc:Endpoints, validation and relations, regenerated on demand.`,
    tileClass: 'bg-sky-600',
  },
  {
    icon: 'fa-solid fa-database',
    title: $localize`:@@devAgent.cap.db:Migrate database`,
    description: $localize`:@@devAgent.cap.dbDesc:Apply schema changes safely to your data.`,
    tileClass: 'bg-emerald-600',
  },
  {
    icon: 'fa-solid fa-rocket',
    title: $localize`:@@devAgent.cap.deploy:Deploy (DevOps)`,
    description: $localize`:@@devAgent.cap.deployDesc:Build, ship and health-check your server.`,
    tileClass: 'bg-slate-700',
  },
];

/** A selectable job card on the configuration screen. */
export interface DevAgentActionOption {
  value: DevAgentAction;
  title: string;
  description: string;
  icon: string;
  tileClass: string;
  /** Step ids included in this job (used for the mini pipeline preview). */
  stepIds: number[];
  badge?: string;
}

/** Job options rendered as cards. */
export const DEV_AGENT_ACTION_OPTIONS: readonly DevAgentActionOption[] = [
  {
    value: 'generateAndDeploy',
    title: $localize`:@@devAgent.action.full:Regenerate code & deploy`,
    description: $localize`:@@devAgent.action.fullDesc:Full pipeline: regenerate server & API code, migrate the database, deploy and verify.`,
    icon: 'fa-solid fa-wand-magic-sparkles',
    tileClass: 'bg-primary',
    stepIds: [1, 2, 3, 4, 5, 6],
    badge: $localize`:@@devAgent.action.recommended:Recommended`,
  },
  {
    value: 'generateOnly',
    title: $localize`:@@devAgent.action.generate:Regenerate code only`,
    description: $localize`:@@devAgent.action.generateDesc:Regenerate server & API code and migrate the database without deploying.`,
    icon: 'fa-solid fa-code',
    tileClass: 'bg-sky-600',
    stepIds: [1, 2, 3, 4],
  },
  {
    value: 'deployOnly',
    title: $localize`:@@devAgent.action.deploy:Deploy only`,
    description: $localize`:@@devAgent.action.deployDesc:Ship the existing code to your server and run a health check. Nothing is regenerated.`,
    icon: 'fa-solid fa-rocket',
    tileClass: 'bg-emerald-600',
    stepIds: [5, 6],
  },
];

/**
 * PURE FUNCTION: Resolves the visual state of a pipeline step.
 * @param {number} stepId - Step id.
 * @param {number} activeStepId - Currently active step id.
 * @param {boolean} isComplete - Whether the run completed successfully.
 * @param {boolean} hasError - Whether the run failed.
 * @param {number[]} skippedSteps - Step ids skipped for the selected job.
 * @returns {DevAgentStepState} The step state.
 */
export function getDevAgentStepState(
  stepId: number,
  activeStepId: number,
  isComplete: boolean,
  hasError: boolean,
  skippedSteps: number[],
): DevAgentStepState {
  if (skippedSteps.includes(stepId)) return 'skipped';
  if (hasError && activeStepId === stepId) return 'error';
  if (isComplete || activeStepId > stepId) return 'done';
  if (activeStepId === stepId) return hasError ? 'error' : 'active';
  return 'pending';
}

/**
 * PURE FUNCTION: Computes the overall progress percentage of a run, ignoring skipped steps.
 * @param {number[]} stepIds - All step ids in order.
 * @param {number} activeStepId - Currently active step id.
 * @param {boolean} isComplete - Whether the run completed.
 * @param {number[]} skippedSteps - Skipped step ids.
 * @returns {number} Progress percentage 0–100.
 */
export function computeDevAgentProgress(
  stepIds: number[],
  activeStepId: number,
  isComplete: boolean,
  skippedSteps: number[],
): number {
  if (isComplete) return 100;
  const relevant = stepIds.filter((id) => !skippedSteps.includes(id));
  if (relevant.length === 0) return 0;
  const done = relevant.filter((id) => id < activeStepId).length;
  // Count the active step as half done so the bar visibly moves while it works
  const activeBonus = relevant.includes(activeStepId) ? 0.5 : 0;
  return Math.round(((done + activeBonus) / relevant.length) * 100);
}

/**
 * PURE FUNCTION: Resolves the overall run status for the drawer header.
 * @param {boolean} showConfig - Whether the configuration screen is shown.
 * @param {boolean} isComplete - Whether the run completed.
 * @param {boolean} hasError - Whether the run failed.
 * @returns {DevAgentRunStatus} The run status.
 */
export function getDevAgentRunStatus(
  showConfig: boolean,
  isComplete: boolean,
  hasError: boolean,
): DevAgentRunStatus {
  if (showConfig) return 'ready';
  if (hasError) return 'failed';
  if (isComplete) return 'done';
  return 'running';
}

/**
 * PURE FUNCTION: Builds the Loopback/Nest API filter query for checking the code generation process status.
 * @param {string} startTimeIso - ISO timestamp string of when the process started.
 * @returns {string} The stringified filter JSON object.
 */
export function buildProcessStatusFilter(startTimeIso: string): string {
  return JSON.stringify({
    where: {
      or: [
        { createdDate: { gt: startTimeIso } },
        { updatedDate: { gt: startTimeIso } },
      ],
    },
  });
}

/**
 * PURE FUNCTION: Extracts the first object from a wrapped array response, or returns the object itself.
 * @param {any} response - The API response.
 * @returns {any} The unwrapped object or null.
 */
export function unwrapApiResponse(response: any): any {
  if (Array.isArray(response) && response.length > 0) return response[0];
  if (response && !Array.isArray(response)) return response;
  return null;
}

/**
 * PURE FUNCTION: Formats sub-processes to ensure previous steps are marked as successful
 * if a newer step exists and the previous one wasn't explicitly a failure.
 * @param {any[]} processes - The array of sub-processes.
 * @returns {any[]} The logically formatted array of sub-processes.
 */
export function formatSubProcesses(processes: any[]): any[] {
  if (!processes || !Array.isArray(processes)) return [];

  return processes.map((proc, index) => {
    const isLast = index === processes.length - 1;
    // If it's not the last item, and it was still "in-progress", it effectively finished to allow the next step
    const status =
      !isLast && proc.status === 'in-progress' ? 'successful' : proc.status;
    return { ...proc, status };
  });
}

/**
 * Interface representing a single parsed line of log output.
 */
export interface LogLine {
  lineNumber: number;
  timestamp: string;
  content: string;
}

/**
 * Interface representing a categorized step in the deployment workflow.
 */
export interface LogStep {
  name: string;
  lines: LogLine[];
  isExpanded: boolean;
}

/**
 * Interface representing the fully structured and parsed deployment logs.
 */
export interface ParsedLogs {
  header: string;
  steps: LogStep[];
}

/**
 * PURE FUNCTION: Parses raw, tab-separated deployment logs into a structured, hierarchical format.
 * Strips out Byte Order Marks (BOM), extracts timestamps, and removes basic ANSI escape codes.
 * @param {string} rawLogs - The raw string payload containing the logs.
 * @returns {ParsedLogs | null} The structured logs mapping.
 */
export function parseDeploymentLogs(rawLogs: string): ParsedLogs | null {
  if (!rawLogs || typeof rawLogs !== 'string') return null;

  const lines = rawLogs.split('\n');
  let header = 'Deployment Logs';
  const stepsMap: Map<string, LogLine[]> = new Map();
  const stepsOrder: string[] = [];

  // Regex to extract standard ISO timestamp prefix
  const timeRegex = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z)\s*(.*)/;

  for (const rawLine of lines) {
    if (!rawLine.trim()) continue;

    // Strip BOM (ï»¿) if present
    const cleanLine = rawLine.replace(/^\xEF\xBB\xBF/, '');

    // Logs are formatted as: ActionName \t StepName \t Timestamp+Content
    const parts = cleanLine.split('\t');

    if (parts.length >= 3) {
      header = parts[0].trim();
      const stepName = parts[1].trim();
      const rest = parts.slice(2).join('\t').trim();

      let timestamp = '';
      let content = rest;

      const match = rest.match(timeRegex);
      if (match) {
        timestamp = match[1];
        content = match[2];
      }

      // Strip basic ANSI escape codes (colors, bold, etc.) for clean rendering
      // eslint-disable-next-line no-control-regex
      content = content.replace(/\x1B\[[0-9;]*[a-zA-Z]/g, '');

      if (!stepsMap.has(stepName)) {
        stepsMap.set(stepName, []);
        stepsOrder.push(stepName);
      }

      const stepLines = stepsMap.get(stepName)!;
      stepLines.push({
        lineNumber: stepLines.length + 1,
        timestamp: timestamp,
        content: content,
      });
    } else {
      // Fallback for lines that might lack the tab delimiters
      if (stepsOrder.length > 0) {
        const lastStep = stepsOrder[stepsOrder.length - 1];
        const stepLines = stepsMap.get(lastStep)!;
        stepLines.push({
          lineNumber: stepLines.length + 1,
          timestamp: '',
          content: cleanLine,
        });
      }
    }
  }

  const steps: LogStep[] = stepsOrder.map((stepName) => ({
    name: stepName,
    lines: stepsMap.get(stepName)!,
    isExpanded: true,
  }));

  // Auto-expand the final step to show the most recent activity
  if (steps.length > 0) {
    steps[steps.length - 1].isExpanded = true;
  }

  return { header, steps };
}
